import * as React from 'react';
import {
  IFieldError,
  IFieldWarning,
  FormContextType,
  FieldValidateFnType,
} from '../../Types/FormTypes';
import { ValueType } from '../../Utils/Formatters/FormatterTypes';
import FormContext from './Contexts/FormContext';

interface IChildInput {
  value?: any;
  fieldErrors?: IFieldError[];
  warnings?: IFieldWarning[];
  onChange: (value: any) => void;
  onBlur: (value?: ValueType) => void;
  touched: boolean;
  form: FormContextType;
}

interface IProps {
  /**
   * Field name
   */
  name: string;
  /**
   * Children render function
   */
  children: (input: IChildInput) => React.ReactNode;
  /**
   * Validate a single field value independently
   */
  validate?: FieldValidateFnType;
}

interface IInnerProps extends IProps {
  formContext: FormContextType;
}

/**
 * This component should only be used inside a Form component
 */
class EDField extends React.Component<IInnerProps> {
  constructor(props: IInnerProps) {
    super(props);
    props.formContext.registerField(props.name, { validate: props.validate });
  }

  componentDidUpdate(prevProps: IInnerProps) {
    // If fields name changes, unregister the old name and register with the new name
    if (this.props.name !== prevProps.name) {
      this.props.formContext.unregisterField(prevProps.name);
      this.props.formContext.registerField(this.props.name, {
        validate: this.props.validate,
      });
    }

    // If fields validate function changes, register field again to overwrite old validate function
    if (this.props.validate !== prevProps.validate) {
      this.props.formContext.registerField(this.props.name, {
        validate: this.props.validate,
      });
    }
  }

  componentWillUnmount() {
    this.props.formContext.unregisterField(this.props.name);
  }

  handleChange = (value: any) => {
    this.props.formContext.handleChange(this.props.name, value);
  };

  /** If no value is provided, use current value */
  handleBlur = (value?: ValueType) => {
    this.props.formContext.handleBlur(
      this.props.name,
      value || this.props.formContext.values[this.props.name]
    );
  };

  render() {
    return this.props.children({
      value: this.props.formContext.values[this.props.name],
      fieldErrors: this.props.formContext.errors[this.props.name],
      warnings: this.props.formContext.warnings[this.props.name],
      onChange: this.handleChange,
      onBlur: this.handleBlur,
      touched: Boolean(this.props.formContext.touched[this.props.name]),
      form: this.props.formContext,
    });
  }
}

const EDFieldWithFormContextConsumer = (props: IProps) => {
  return (
    <FormContext.Consumer>
      {(formContext: FormContextType) => (
        <EDField {...props} formContext={formContext}>
          {props.children}
        </EDField>
      )}
    </FormContext.Consumer>
  );
};

export default EDFieldWithFormContextConsumer;
